import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Dropdown, List, Button, Spin } from 'antd';
import { BellIcon } from '@heroicons/react/24/outline';
import { apiService } from '../../services/api';
import { notificationQueryKeys } from '../../lib/queryClient';
import { useAuth } from '../../contexts/AuthContext';

interface NotificationItem {
  id: number;
  title: string;
  message?: string;
  isRead: boolean;
  createdAt: string;
  type?: string;
  relatedId?: number;
  linkUrl?: string;
}

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const resolveNotificationTarget = (item: NotificationItem): string | undefined => {
    if (item.linkUrl) return item.linkUrl;

    const role = user?.role;
    const type = item.type;

    if (role === 'Mentor') {
      if (type === 'BookingCreated' || type === 'BookingCancelled') return '/profile?tab=appointments';
      if (type === 'BootcampApplication') return '/profile?tab=bootcamps';
      if (type === 'ReferenceAccepted' || type === 'ReferenceRejected') return '/profile?tab=referanslar';
      if (type === 'AdvertisementApproved' || type === 'AdvertisementRejected') return '/profile?tab=advertisements';
      if (type === 'SubscriptionActivated') return '/profile?tab=subscription';
      if (type === 'AddedToPool' || type === 'AddedToFavorites' || type === 'InterviewScheduled' || type === 'InterviewUpdated' || type === 'InterviewCompleted' || type === 'InterviewCancelled' || type === 'JobApplicationStatus') {
        return '/profile?tab=interviews';
      }
    }

    if (role === 'Company') {
      if (type === 'JobApplicationSubmitted' || type === 'JobApplication' || type === 'InterviewScheduled' || type === 'InterviewCompleted' || type === 'InterviewCancelled' || type === 'JobApplicationStatus') {
        return '/company-panel?tab=applications';
      }
      if (type === 'SubscriptionActivated') return '/company-panel?tab=subscription';
    }

    if (role === 'Mentee') {
      if (type === 'BookingApproved' || type === 'BookingRejected' || type === 'BookingCancelled' || type === 'BookingCompleted' || type === 'BootcampApproved' || type === 'BootcampRejected' || type === 'BootcampCompleted') {
        return '/mentee-profile?tab=appointments';
      }
      if (type === 'ReferenceRequest') return '/mentee-profile?tab=referanslar';
      if (type === 'InterviewScheduled' || type === 'InterviewUpdated' || type === 'InterviewCompleted' || type === 'InterviewCancelled' || type === 'JobApplicationStatus' || type === 'AddedToPool' || type === 'AddedToFavorites') {
        return '/mentee-profile?tab=interviews';
      }
      if (type === 'SubscriptionActivated') return '/mentee-profile?tab=subscription';
    }

    return undefined;
  };

  const { data: countRes, isLoading: countLoading } = useQuery({
    queryKey: notificationQueryKeys.unreadCount,
    queryFn: async () => {
      const r = await apiService.getNotificationUnreadCount();
      if (!r.success) throw new Error(r.message);
      return r.data ?? 0;
    },
    refetchInterval: 60000,
  });
  const unreadCount = countRes ?? 0;

  const { data: listRes, isLoading: listLoading } = useQuery({
    queryKey: notificationQueryKeys.list(1, 10),
    queryFn: async () => {
      const r = await apiService.getNotifications(1, 10);
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    enabled: dropdownOpen,
  });
  const notifications: NotificationItem[] = Array.isArray((listRes as { data?: NotificationItem[] })?.data)
    ? (listRes as { data: NotificationItem[] }).data
    : [];

  const markReadMutation = useMutation({
    mutationFn: (id: number) => apiService.markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const markAllReadMutation = useMutation({
    mutationFn: () => apiService.markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const onOpenChange = (open: boolean) => setDropdownOpen(open);

  const dropdownContent = (
    <div style={{ width: 360, maxHeight: 400, overflow: 'auto', background: '#fff', borderRadius: 8, boxShadow: '0 6px 16px rgba(0,0,0,0.08)' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Bildirimler</span>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={() => markAllReadMutation.mutate()} loading={markAllReadMutation.isPending}>
            Tümünü okundu işaretle
          </Button>
        )}
      </div>
      {listLoading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><Spin size="small" /></div>
      ) : notifications.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>Bildirim yok</div>
      ) : (
        <List
          size="small"
          dataSource={notifications}
          renderItem={(item: NotificationItem) => (
            <List.Item
              key={item.id}
              style={{
                padding: '10px 16px',
                background: item.isRead ? undefined : 'rgba(22, 119, 255, 0.04)',
                cursor: resolveNotificationTarget(item) ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (!item.isRead) markReadMutation.mutate(item.id);
                const targetUrl = resolveNotificationTarget(item);
                if (targetUrl) {
                  if (targetUrl.startsWith('http')) window.location.href = targetUrl;
                  else navigate(targetUrl);
                }
              }}
            >
              <List.Item.Meta
                title={<span style={{ fontSize: 13, fontWeight: item.isRead ? 400 : 500 }}>{item.title}</span>}
                description={
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {item.message && item.message.slice(0, 80)}{item.message && item.message.length > 80 ? '...' : ''}
                    <br />
                    <span style={{ fontSize: 11 }}>{new Date(item.createdAt).toLocaleDateString('tr-TR')} {new Date(item.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                  </span>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      onOpenChange={onOpenChange}
    >
      <div className="flex items-center justify-center cursor-pointer hover:bg-gray-100 rounded-full p-2" style={{ position: 'relative' }}>
        <Badge count={countLoading ? 0 : unreadCount} size="small" offset={[-2, 2]}>
          <BellIcon className="w-6 h-6" style={{ color: 'var(--text-secondary)' }} />
        </Badge>
      </div>
    </Dropdown>
  );
};

export default NotificationBell;
